/**
 * useUserPermissions
 *
 * React hook that derives the current user's UI permissions from the
 * authenticated user profile returned by `useUserInfo()`.
 *
 * Composes the pure predicates from `shared/utils/permissions` with the
 * TanStack Query cache so that components never need to manually extract
 * `profile.name` or call `useMemo` themselves.
 *
 * **When `profile.name` is absent from user-info** (edge case where the
 * token is valid but the profile hasn't been assigned yet), access control
 * is deferred to the backend via tab-level 401 handling — the page is NOT
 * blocked on the frontend.
 *
 * @module shared/hooks/useUserPermissions
 *
 * @example
 * ```tsx
 * const { canView, canCreate, canEdit, isLoading } = useUserPermissions();
 *
 * if (isLoading) return <Skeleton />;
 * if (!canView)  return <AccessDenied />;
 * ```
 */
import { useMemo } from 'react';

import { useUserInfo } from '@/features/auth/hooks/useUserInfo';
import {
  canViewUsers,
  canCreateInvites,
  canEditUsers,
  canDeleteUsers,
  canSwitchEntities,
  canAccessUserManagement,
  isInternalProfile,
} from '@/shared/utils/permissions';

export interface UserPermissions {
  /** Whether the user may view the User Management screen */
  canView: boolean;
  /** Whether the user may send invitations */
  canCreate: boolean;
  /** Whether the user may edit profiles / delete users */
  canEdit: boolean;
  /** Whether the user may delete users (admin-level only) */
  canDelete: boolean;
  /** Whether the user may switch between entities (platforms) */
  canSwitchEntity: boolean;
  /** Whether the user may access the User Management screen */
  canManageUsers: boolean;
  /** Whether the user has an internal profile */
  isInternal: boolean;
  /** Whether the authenticated user belongs to a federation */
  isFederated: boolean;
  /** True while user-info is still being fetched */
  isLoading: boolean;
}

/**
 * Derives UI permissions for the authenticated user.
 *
 * @returns `{ canView, canCreate, canEdit, isLoading }`
 */
export function useUserPermissions(): UserPermissions {
  const { data: userInfo, isLoading } = useUserInfo();

  const profileName = userInfo?.profile?.name;
  const isFederated = userInfo?.isFederated === true;
  const isConfirmedNonFederated = userInfo?.isFederated === false;

  const canView = useMemo(
    // When profileName is absent, allow through — backend 401 will gate access
    () =>
      isConfirmedNonFederated &&
      !isFederated &&
      (!profileName || canViewUsers(profileName)),
    [isConfirmedNonFederated, isFederated, profileName]
  );

  const canCreate = useMemo(
    () =>
      isConfirmedNonFederated && !isFederated && canCreateInvites(profileName),
    [isConfirmedNonFederated, isFederated, profileName]
  );

  const canEdit = useMemo(
    () => isConfirmedNonFederated && !isFederated && canEditUsers(profileName),
    [isConfirmedNonFederated, isFederated, profileName]
  );

  const canDelete = useMemo(
    () =>
      isConfirmedNonFederated && !isFederated && canDeleteUsers(profileName),
    [isConfirmedNonFederated, isFederated, profileName]
  );

  const canSwitchEntity = useMemo(
    () => canSwitchEntities(profileName),
    [profileName]
  );

  // RN09 — para usuário de cliente federado a Gestão de acessos não existe na
  // plataforma, independentemente do perfil: a administração de usuários
  // acontece no IdP do cliente. A negativa vem antes da regra por perfil, que
  // de outro modo liberaria um Gestor federado.
  const canManageUsers = useMemo(
    () =>
      isConfirmedNonFederated &&
      !isFederated &&
      canAccessUserManagement(profileName),
    [isConfirmedNonFederated, isFederated, profileName]
  );

  const isInternal = useMemo(
    () => isInternalProfile(profileName),
    [profileName]
  );

  return {
    canView,
    canCreate,
    canEdit,
    canDelete,
    canSwitchEntity,
    canManageUsers,
    isInternal,
    isFederated,
    isLoading,
  };
}
